<template>
  <div class="select-none">
    <!-- Folder row -->
    <button
      type="button"
      data-testid="tree-folder"
      :data-name="folder.name"
      :aria-expanded="isOpenRef"
      class="group flex h-9 w-full items-center gap-1.5 text-left font-semibold hover:bg-primary/50 cursor-pointer transition-colors"
      :style="{ paddingLeft: `${paddingLeft}px`, paddingRight: '16px' }"
      @click="isOpenRef = !isOpenRef"
    >
      <MdiIcon
        :path="isOpenRef ? mdiChevronDown : mdiChevronRight"
        size="30"
        class="transition-transform duration-200"
      />
      <MdiIcon :path="isOpenRef ? mdiFolderOpen : mdiFolder" size="20" class="text-accent/80" />
      <span class="min-w-0 flex-1 truncate">{{ folder.name }}</span>
      <!-- Context menu (three-dot) — shown for project folders (depth >= 1, no "/")
           and subfolders (composite id with "/"); the menu items differ by kind. -->
      <div
        v-if="depth >= 1"
        class="transition-opacity flex-shrink-0 flex items-center relative"
        :class="isFolderMenuActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
      >
        <button
          type="button"
          data-testid="tree-folder-menu"
          :data-name="folder.name"
          class="flex items-center justify-center p-1 rounded hover:bg-primary transition-colors"
          @click.stop="toggleMenu(folderMenuTarget, $event)"
        >
          <MdiIcon :path="mdiDotsVertical" size="20" />
        </button>
      </div>
    </button>

    <!-- Children when open -->
    <div v-if="isOpenRef">
      <!-- Nested subfolders -->
      <SidebarTreeNode
        v-for="sub in folder.folders"
        :key="sub.id"
        :folder="sub"
        :depth="depth + 1"
        :active-file="activeFile"
        :collapse-signal="props.collapseSignal"
        @update:active-file="$emit('update:activeFile', $event)"
        @open-new-file="$emit('open-new-file')"
        @rename-file="(uid, newName) => $emit('rename-file', uid, newName)"
        @delete-file="$emit('delete-file', $event)"
        @move-file="$emit('move-file', $event)"
        @change-version="$emit('change-version', $event)"
        @view-history="$emit('view-history', $event)"
        @change-permissions="$emit('change-permissions', $event)"
        @show-submissions="$emit('show-submissions', $event)"
        @rename-folder="$emit('rename-folder', $event)"
        @delete-folder="$emit('delete-folder', $event)"
        @rename-project="(id, name) => $emit('rename-project', id, name)"
        @share-project="$emit('share-project', $event)"
        @delete-project="$emit('delete-project', $event)"
      />

      <!-- Files in this folder -->
      <div v-for="file in folder.files" :key="file.path">
        <button
          type="button"
          data-testid="tree-file"
          :data-path="file.path"
          class="group flex h-8 w-full items-center justify-between border border-transparent text-left hover:bg-primary/60 cursor-pointer transition-all"
          :class="{
            'border-accent bg-primary text-accent': activeFile === fileUidFor(file.path),
          }"
          :style="{ paddingLeft: `${filePaddingLeft}px`, paddingRight: '16px' }"
          @click="$emit('update:activeFile', fileUidFor(file.path))"
        >
          <div class="flex items-center gap-2 min-w-0">
            <MdiIcon
              :path="mdiFileOutline"
              size="20"
              :class="activeFile === fileUidFor(file.path) ? 'text-accent' : 'text-accent/60'"
            />
            <span class="min-w-0 truncate">{{ file.name }}</span>
          </div>
          <!-- Context Menu (three-dot) -->
          <div
            class="transition-opacity flex-shrink-0 flex items-center relative"
            :class="
              isFileMenuActive(file.path) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            "
          >
            <button
              type="button"
              class="flex items-center justify-center p-1 rounded hover:bg-primary transition-colors"
              @click.stop="toggleMenu({ kind: 'file', id: file.path }, $event)"
            >
              <MdiIcon :path="mdiDotsVertical" size="20" />
            </button>
          </div>
        </button>
      </div>
    </div>
  </div>

  <!-- Action menu (teleported to body) -->
  <Teleport to="body">
    <div
      v-if="activeMenu"
      class="fixed w-64 bg-secondary border border-tertiary rounded-standard shadow-2xl z-[9999] py-1"
      :style="{ top: menuY + 'px', left: menuX + 'px' }"
      @click.stop
    >
      <!-- File actions -->
      <ul
        v-if="activeMenu.kind === 'file'"
        class="text-[13px] text-text-main font-semibold flex flex-col"
      >
        <li
          class="px-4 py-2 hover:bg-primary/60 cursor-pointer transition-colors text-left"
          @click="handleFileAction('rename-file', activeMenu.id, '')"
        >
          Umbenennen
        </li>
        <li
          class="px-4 py-2 hover:bg-primary/60 cursor-pointer transition-colors text-left text-accent"
          @click="handleFileAction('delete-file', activeMenu.id)"
        >
          Löschen
        </li>
        <li
          class="px-4 py-2 hover:bg-primary/60 cursor-pointer transition-colors text-left"
          @click="handleFileAction('move-file', activeMenu.id)"
        >
          Bewegen nach
        </li>
        <li
          class="px-4 py-2 hover:bg-primary/60 cursor-pointer transition-colors text-left"
          @click="handleFileAction('show-submissions', activeMenu.id)"
        >
          Abgaben anzeigen
        </li>
      </ul>

      <!-- Subfolder actions (rename / delete folder) -->
      <ul
        v-else-if="activeMenu.kind === 'folder'"
        class="text-[13px] text-text-main font-semibold flex flex-col"
      >
        <li
          class="px-4 py-2 hover:bg-primary/60 cursor-pointer transition-colors text-left"
          @click="handleFolderAction('rename-folder', activeMenu.id)"
        >
          Ordner umbenennen
        </li>
        <li
          class="px-4 py-2 hover:bg-primary/60 cursor-pointer transition-colors text-left text-accent"
          @click="handleFolderAction('delete-folder', activeMenu.id)"
        >
          Ordner löschen
        </li>
      </ul>

      <!-- Project folder actions (rename / share / delete project) -->
      <ul v-else class="text-[13px] text-text-main font-semibold flex flex-col">
        <li
          class="px-4 py-2 hover:bg-primary/60 cursor-pointer transition-colors text-left flex items-center gap-2"
          @click="handleProjectAction('rename-project', activeMenu.id, folder.name)"
        >
          <MdiIcon :path="mdiPencilOutline" size="16" />
          Umbenennen
        </li>
        <li
          data-testid="project-menu-share"
          class="px-4 py-2 hover:bg-primary/60 cursor-pointer transition-colors text-left flex items-center gap-2"
          @click="handleProjectAction('share-project', activeMenu.id)"
        >
          <MdiIcon :path="mdiShareVariantOutline" size="16" />
          Teilen
        </li>
        <li
          data-testid="project-menu-versions"
          class="px-4 py-2 hover:bg-primary/60 cursor-pointer transition-colors text-left flex items-center gap-2"
          @click="handleProjectAction('change-version', activeMenu.id)"
        >
          <MdiIcon :path="mdiHistory" size="16" />
          Version ändern
        </li>
        <li
          class="px-4 py-2 hover:bg-primary/60 cursor-pointer transition-colors text-left flex items-center gap-2"
          @click="handleProjectAction('change-permissions', activeMenu.id)"
        >
          <MdiIcon :path="mdiLockOutline" size="16" />
          Berechtigungen ändern
        </li>
        <li
          class="px-4 py-2 hover:bg-primary/60 cursor-pointer transition-colors text-left text-accent flex items-center gap-2"
          @click="handleProjectAction('delete-project', activeMenu.id)"
        >
          <MdiIcon :path="mdiTrashCanOutline" size="16" />
          Löschen
        </li>
      </ul>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onUnmounted, watch } from 'vue'
import {
  mdiChevronRight,
  mdiChevronDown,
  mdiFolder,
  mdiFolderOpen,
  mdiFileOutline,
  mdiDotsVertical,
  mdiPencilOutline,
  mdiShareVariantOutline,
  mdiTrashCanOutline,
  mdiHistory,
  mdiLockOutline,
} from '@mdi/js'
import MdiIcon from '@/components/MdiIcon.vue'
import type { TreeFolder } from '@/services/projectService'
import { fileUid } from '@/services/projectService'

const props = withDefaults(
  defineProps<{
    folder: TreeFolder
    depth?: number
    // The uid (projectId + path) of the currently selected file — NOT a bare
    // path, so a file is only highlighted in the one project it belongs to.
    activeFile: string
    collapseSignal?: number
  }>(),
  {
    depth: 0,
    collapseSignal: 0,
  }
)

const emit = defineEmits<{
  (e: 'update:activeFile', uid: string): void
  (e: 'open-new-file'): void
  (e: 'rename-file', uid: string, newName: string): void
  (e: 'delete-file', uid: string): void
  (e: 'move-file', uid: string): void
  (e: 'change-version', projectId: string): void
  (e: 'view-history', uid: string): void
  (e: 'change-permissions', projectId: string): void
  (e: 'show-submissions', uid: string): void
  (e: 'rename-folder', folderId: string): void
  (e: 'delete-folder', folderId: string): void
  (e: 'rename-project', projectId: string, name: string): void
  (e: 'share-project', projectId: string): void
  (e: 'delete-project', projectId: string): void
}>()

// Local open state: open at depth 0, closed otherwise
const isOpenRef = ref(props.depth === 0)

// "Collapse all" broadcast: when the parent bumps collapseSignal, collapse
// every folder except the namespace root (depth 0) so the project list stays
// visible but all project/subfolder contents are hidden.
watch(
  () => props.collapseSignal,
  () => {
    if (props.depth >= 1) isOpenRef.value = false
  }
)

// Padding calculations
const paddingLeft = computed(() => 8 + props.depth * 20)
const filePaddingLeft = computed(() => 40 + props.depth * 20)

// A real, renameable sub-folder has a composite id ("<projectId>/<prefix>"). The
// namespace root and top-level project folders have no "/" and get no menu.
const isSubfolder = computed(() => props.folder.id.includes('/'))

// The project a file in this folder belongs to. Folder ids are either a bare
// project id ("<projectId>") or a composite subfolder id ("<projectId>/<prefix>"),
// so the project id is always the part before the first "/".
const sourceProjectId = computed(() =>
  props.folder.id.includes('/') ? props.folder.id.split('/')[0] : props.folder.id
)

// The unique id of a file in this folder: its owning project id + its path. Two
// projects can hold a file with the same path, so this — not the path alone — is
// what selection and file actions are keyed by, keeping them project-scoped.
function fileUidFor(path: string): string {
  return fileUid(sourceProjectId.value, path)
}

// Action menu state — one menu per node, covering files, subfolders and project
// folders. Subfolders get rename/delete; project folders (top-level, no "/")
// get share/delete-project.
type MenuTarget = { kind: 'file' | 'folder' | 'project'; id: string }
type FileEvent = 'rename-file' | 'delete-file' | 'move-file' | 'view-history' | 'show-submissions'

const folderMenuTarget = computed<MenuTarget>(() =>
  isSubfolder.value
    ? { kind: 'folder', id: props.folder.id }
    : { kind: 'project', id: props.folder.id }
)

const activeMenu = ref<MenuTarget | null>(null)
const menuX = ref(0)
const menuY = ref(0)

// Approximate menu size for viewport-overflow clamping (w-64 = 256px; up to 7 items).
const MENU_WIDTH = 256
const MENU_HEIGHT = 300
const MENU_GAP = 8

function isFileMenuActive(path: string): boolean {
  return activeMenu.value?.kind === 'file' && activeMenu.value.id === path
}

// True while this folder's own kebab menu is open (subfolder or project kind).
const isFolderMenuActive = computed(
  () => activeMenu.value?.kind === 'folder' || activeMenu.value?.kind === 'project'
)

async function openMenu(target: MenuTarget, trigger: HTMLElement) {
  activeMenu.value = target
  const rect = trigger.getBoundingClientRect()

  // Prefer opening to the right of the trigger; flip left if it would overflow.
  let x = rect.right + MENU_GAP
  if (x + MENU_WIDTH > window.innerWidth) {
    x = Math.max(MENU_GAP, rect.left - MENU_WIDTH - MENU_GAP)
  }
  // Prefer aligning with the trigger top; lift it up if it would overflow.
  let y = rect.top
  if (y + MENU_HEIGHT > window.innerHeight) {
    y = Math.max(MENU_GAP, window.innerHeight - MENU_HEIGHT - MENU_GAP)
  }
  menuX.value = x
  menuY.value = y

  await nextTick()
  document.addEventListener('click', closeMenu)
  document.addEventListener('keydown', onKeydown)
}

function toggleMenu(target: MenuTarget, event: Event) {
  if (activeMenu.value?.kind === target.kind && activeMenu.value.id === target.id) {
    closeMenu()
  } else {
    openMenu(target, event.currentTarget as HTMLElement)
  }
}

function closeMenu() {
  if (activeMenu.value === null) return
  activeMenu.value = null
  document.removeEventListener('click', closeMenu)
  document.removeEventListener('keydown', onKeydown)
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') closeMenu()
}

// `id` is the file's local path (the menu target); actions are emitted with the
// full uid so the parent operates on the correct project's copy.
function handleFileAction(event: FileEvent, id: string, arg = '') {
  const uid = fileUidFor(id)
  switch (event) {
    case 'rename-file':
      emit('rename-file', uid, arg)
      break
    case 'move-file':
      emit('move-file', uid)
      break
    case 'delete-file':
      emit('delete-file', uid)
      break
    case 'view-history':
      emit('view-history', uid)
      break
    case 'show-submissions':
      emit('show-submissions', uid)
      break
  }
  closeMenu()
}

function handleFolderAction(event: 'rename-folder' | 'delete-folder', id: string) {
  if (event === 'rename-folder') emit('rename-folder', id)
  else emit('delete-folder', id)
  closeMenu()
}

function handleProjectAction(
  event:
    'rename-project' | 'share-project' | 'change-version' | 'change-permissions' | 'delete-project',
  id: string,
  name = ''
) {
  if (event === 'rename-project') emit('rename-project', id, name)
  else if (event === 'share-project') emit('share-project', id)
  else if (event === 'change-version') emit('change-version', id)
  else if (event === 'change-permissions') emit('change-permissions', id)
  else emit('delete-project', id)
  closeMenu()
}

// Listeners are attached only while a menu is open (see openMenu); make sure
// they never outlive the component if it unmounts with a menu still open.
onUnmounted(closeMenu)
</script>
